import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import type { LocationSuggestion } from "@/lib/places.ts";
import {
  getLocationDisplayValue,
  getLocationMapsUrl,
  parseLocationData,
} from "@/shared/locationDisplay.ts";

export type { LocationData } from "@/shared/locationDisplay.ts";
export { getLocationDisplayValue, parseLocationData };

/**
 * Serialize a picker selection back to the canonical stored shape so that
 * picking a place from the Google Places dropdown preserves the same
 * `{name, address, coordinates, placeId}` JSON the backend emits when it
 * resolves a location (see api `formatResolvedLocation`).
 */
export const serializeLocationFromSuggestion = (loc: LocationSuggestion): string => {
  if (loc.name && loc.address && loc.name !== loc.address) {
    return JSON.stringify({
      name: loc.name,
      address: loc.address,
      coordinates: loc.location
        ? { lat: loc.location.latitude, lng: loc.location.longitude }
        : undefined,
      placeId: loc.placeId,
    });
  }
  return loc.address || loc.name;
};

export const LocationDisplay = ({
  location,
  className = "link text-sm @md:text-base",
}: {
  location: string;
  /** Overrides the anchor's classes so hosts with their own row typography
   *  (the event detail panel) don't have to fork this markup. */
  className?: string;
}) => {
  const locationData = parseLocationData(location);
  const googleMapsLink = getLocationMapsUrl(location);

  if (locationData) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a href={googleMapsLink} target="_blank" rel="noopener noreferrer" className={className}>
            {getLocationDisplayValue(location)}
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>{locationData.address}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Fallback for plain text locations - still make them clickable
  return (
    <a href={googleMapsLink} target="_blank" rel="noopener noreferrer" className={className}>
      {location}
    </a>
  );
};
